import { createContext, useContext, useReducer, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

const initialState = {
    user: null,
    isAuthenticated: false,
    loading: true,
    error: null,
};

const authReducer = (state, action) => {
    switch (action.type) {
        case 'AUTH_SUCCESS':
            return {
                ...state,
                user: action.payload.user,
                isAuthenticated: true,
                loading: false,
                error: null,
            };
        case 'AUTH_FAIL':
            return {
                ...state,
                user: null,
                isAuthenticated: false,
                loading: false,
                error: action.payload,
            };
        case 'LOGOUT':
            return {
                ...state,
                user: null,
                isAuthenticated: false,
                loading: false,
                error: null,
            };
        case 'CLEAR_ERROR':
            return {
                ...state,
                error: null,
            };
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload,
            };
        case 'UPDATE_USER':
            return {
                ...state,
                user: { ...state.user, ...action.payload },
            };
        default:
            return state;
    }
};

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    // Check if user is logged in on mount
    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const response = await authAPI.getMe();
            dispatch({
                type: 'AUTH_SUCCESS',
                payload: {
                    user: response.data.data,
                },
            });
        } catch (error) {
            dispatch({ type: 'AUTH_FAIL', payload: 'Authentication failed' });
        }
    };

    // Note: Login and registration are now handled by the main app at app.rannkly.rank
    // Users should authenticate there before accessing the widget app

    // Logout function - redirects to main app logout
    const logout = () => {
        dispatch({ type: 'LOGOUT' });
        // Redirect to main app for logout to clear shared cookies
        window.location.href = process.env.REACT_APP_MAIN_APP_URL || 'https://app.rannkly.rank';
    };

    // Clear error function
    const clearError = () => {
        dispatch({ type: 'CLEAR_ERROR' });
    };

    // Update user function - refreshes user data from shared API
    const updateUser = async () => {
        try {
            const response = await authAPI.getMe();
            dispatch({
                type: 'UPDATE_USER',
                payload: response.data.data,
            });
        } catch (error) {
            console.error('Failed to update user:', error);
        }
    };

    const value = {
        ...state,
        logout,
        clearError,
        updateUser,
        checkAuth, // Export checkAuth for manual refresh if needed
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 